import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLifeRing, faXmark, faRobot, faTicket } from '@fortawesome/free-solid-svg-icons';
import { api, ApiError } from '../../lib/api/client';
import { collectDiagnostics } from '../../lib/deviceService/diagnostics';

/** Lets any page open the support widget (e.g. the Help page's "Get help" button). */
const SupportContext = createContext<{ openSupport: () => void } | null>(null);
export function useSupport() {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupport must be used within <SupportWidget>');
  return ctx;
}

interface Txn { tcn?: string; captureType: string; state: string; createdOn?: string }
interface TicketResult { ok: boolean; ticketId: string; ticketNum: string }
interface ChatResult { reply: string; sessionId: string; escalated: boolean; ticketNum?: string }
interface ChatMsg { role: 'user' | 'assistant'; content: string; ticketNum?: string }

const CATEGORIES: [string, string][] = [
  ['device', 'Scanner / Device Service'],
  ['transaction', 'A transaction'],
  ['billing', 'Billing / subscription'],
  ['account', 'My account'],
  ['other', 'Something else'],
];

const GREETING =
  "Hi! I'm the EFTCreator assistant. I can help with scanners, the Device Service, transactions, billing, and your account — and I can pull up your own account details. What's going on?";

type Mode = 'chat' | 'ticket';

/**
 * The transcript is kept in sessionStorage as well as in state.
 *
 * The provider sits above <Routes>, so state alone survives navigation — but closing the panel used to
 * wipe it, and the operator has to close it to go wherever the assistant just sent them ("check
 * Resources > Support"). They came back to a fresh greeting with the session id gone, so the assistant had
 * lost all context. sessionStorage additionally survives a reload; per-tab, and cleared on sign-out.
 */
const CHAT_STORE_KEY = 'eftc.support.chat.v1';

interface StoredChat { messages: ChatMsg[]; sessionId?: string }

function loadChat(): StoredChat | null {
  try {
    const raw = sessionStorage.getItem(CHAT_STORE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as StoredChat;
    return Array.isArray(v.messages) && v.messages.length ? v : null;
  } catch {
    return null; // corrupt or unavailable storage is not worth failing the widget over
  }
}

export function SupportWidget({ children }: { children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('chat');

  // Chat state, rehydrated so a conversation survives closing the panel, navigating, or reloading.
  const restored = useRef<StoredChat | null>(loadChat());
  const [messages, setMessages] = useState<ChatMsg[]>(
    restored.current?.messages ?? [{ role: 'assistant', content: GREETING }],
  );
  const [sessionId, setSessionId] = useState<string | undefined>(restored.current?.sessionId);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Ticket-form state
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('device');
  const [message, setMessage] = useState('');
  const [tcn, setTcn] = useState('');
  const [done, setDone] = useState<TicketResult | null>(null);

  const txns = useQuery({
    queryKey: ['support-txns'],
    // GET /transactions is paged; this picker only needs the most recent page.
    queryFn: () => api.get<{ items: Txn[]; cursor: string | null }>('/transactions?limit=25').then((r) => r.items),
    enabled: open && mode === 'ticket',
    staleTime: 60_000,
  });

  /*
   * Collected once per widget mount and resent with every chat turn.
   *
   * The assistant decides when to escalate, so the client cannot know which turn becomes a ticket — and an
   * escalated ticket with no diagnostics is the exact gap this closes. Cached in a ref so a conversation costs
   * one collection, not one per message.
   */
  const diagRef = useRef<Awaited<ReturnType<typeof collectDiagnostics>> | null>(null);
  const diagnostics = async () => (diagRef.current ??= await collectDiagnostics());

  const chat = useMutation({
    mutationFn: async (text: string) =>
      api.post<ChatResult>('/support/chat', {
        message: text,
        ...(sessionId ? { sessionId } : {}),
        diagnostics: await diagnostics(),
      }),
    onSuccess: (r) => {
      setSessionId(r.sessionId);
      setMessages((m) => [...m, { role: 'assistant', content: r.reply, ...(r.ticketNum ? { ticketNum: r.ticketNum } : {}) }]);
    },
    onError: () =>
      setMessages((m) => [...m, { role: 'assistant', content: "Sorry — I hit an error. You can open a support ticket and our team will help." }]),
  });

  const submitTicket = useMutation({
    /*
     * Workstation diagnostics are gathered at submit, not on open: opening the widget must stay instant, and
     * the facts should describe the machine at the moment the customer says something is wrong. Collection
     * never throws and is time-boxed, so a stopped Device Service delays the ticket by a moment rather than
     * blocking it — and "not reachable" is itself the answer to most of these tickets.
     */
    mutationFn: async () =>
      api.post<TicketResult>('/support/ticket', {
        subject: subject.trim(),
        message: message.trim(),
        category,
        ...(tcn ? { transactionTcn: tcn } : {}),
        diagnostics: await diagnostics(),
      }),
    onSuccess: (r) => setDone(r),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, chat.isPending]);

  useEffect(() => {
    try {
      sessionStorage.setItem(CHAT_STORE_KEY, JSON.stringify({ messages, sessionId }));
    } catch { /* storage full or blocked — the in-memory transcript still works */ }
  }, [messages, sessionId]);

  /**
   * Closing hides the panel and clears the ticket FORM, but deliberately keeps the conversation: the
   * assistant routinely tells people to go and look at something, and losing the thread when they do made
   * it start over from the greeting every time.
   */
  function close() {
    setOpen(false);
    setTimeout(() => {
      setMode('chat');
      setDone(null); submitTicket.reset(); setSubject(''); setMessage(''); setTcn(''); setCategory('device');
    }, 200);
  }

  /** Explicit "start over" — the only thing that discards the transcript. */
  function resetChat() {
    setMessages([{ role: 'assistant', content: GREETING }]);
    setSessionId(undefined);
    setInput('');
    try { sessionStorage.removeItem(CHAT_STORE_KEY); } catch { /* ignore */ }
  }

  function sendChat() {
    const text = input.trim();
    if (!text || chat.isPending) return;
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    chat.mutate(text);
  }

  /** Switch to the ticket form, pre-filling the subject from the last thing the user asked. */
  function toTicket() {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUser && !subject) setSubject(lastUser.content.slice(0, 80));
    setMode('ticket');
  }

  const canSend = subject.trim().length > 2 && message.trim().length > 4 && !submitTicket.isPending;

  const openSupport = useCallback(() => { setDone(null); setOpen(true); }, []);
  const ctxValue = useMemo(() => ({ openSupport }), [openSupport]);

  return (
    <SupportContext.Provider value={ctxValue}>
      {children}
      {!open && (
        <button className="support-fab" data-tour="support-fab" onClick={() => setOpen(true)} aria-label="Get help">
          <FontAwesomeIcon icon={faLifeRing} /> <span>Get help</span>
        </button>
      )}

      {open && (
        <div className="support-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div className="support-panel" role="dialog" aria-modal="true" aria-label="Support">
            <div className="support-head">
              <h3>
                <FontAwesomeIcon icon={mode === 'chat' ? faRobot : faLifeRing} />{' '}
                {mode === 'chat' ? 'EFTCreator assistant' : 'Contact support'}
              </h3>
              <button className="support-x" onClick={close} aria-label="Close"><FontAwesomeIcon icon={faXmark} /></button>
            </div>

            {mode === 'chat' ? (
              <div className="support-chat">
                <div className="support-chat-log" ref={scrollRef}>
                  {messages.map((m, i) => (
                    <div key={i} className={`chat-bubble ${m.role}`}>
                      <div className="chat-bubble-body">{m.content}</div>
                      {/*
                        * A created ticket ends the assistant's usefulness for that issue: replies come
                        * from a person, by email and on the Support page — not here. Saying so plainly,
                        * with the place to go, beats the assistant carrying on as though nothing changed.
                        */}
                      {m.ticketNum && (
                        <div className="chat-escalated">
                          <div>
                            <strong>Ticket {m.ticketNum}</strong> is open with our support team.
                          </div>
                          <div className="chat-escalated-sub">
                            They'll reply by email, and you can read and answer them under{' '}
                            <strong>Resources → Support</strong>. Replies to this ticket aren't read here.
                          </div>
                          <Link to="/app/support" className="btn btn-primary btn-sm" onClick={close}>
                            Go to my tickets
                          </Link>
                        </div>
                      )}
                    </div>
                  ))}
                  {chat.isPending && <div className="chat-bubble assistant"><div className="chat-typing"><span /><span /><span /></div></div>}
                </div>
                <form className="support-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(); }}>
                  <input className="input" value={input} placeholder="Ask a question…" autoFocus
                    onChange={(e) => setInput(e.target.value)} />
                  <button className="btn btn-primary btn-sm" type="submit" disabled={!input.trim() || chat.isPending}>Send</button>
                </form>
                <div className="support-chat-foot">
                  <button className="support-switch" onClick={toTicket}>
                    <FontAwesomeIcon icon={faTicket} /> Talk to a person / open a ticket
                  </button>
                  {messages.length > 1 && (
                    <button type="button" className="linklike support-reset" onClick={resetChat}>
                      Start over
                    </button>
                  )}
                </div>
              </div>
            ) : done ? (
              <div className="support-done">
                <div className="support-check">✓</div>
                <h4>We've got your request</h4>
                <p>
                  Your support ticket <strong>{done.ticketNum}</strong> has been created — we'll reply by
                  email shortly. You can also track it from{' '}
                  <Link to="/app/support" className="track-link" onClick={close}>My tickets</Link>.
                </p>
                <button className="btn btn-primary" onClick={close}>Done</button>
              </div>
            ) : (
              <form className="support-form" onSubmit={(e) => { e.preventDefault(); if (canSend) submitTicket.mutate(); }}>
                <p className="support-sub">
                  We'll include your account, plan and recent activity automatically so we can help faster.
                </p>
                <label className="field">
                  <span className="field-label">What do you need help with?</span>
                  <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
                    {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>

                {(category === 'transaction' || (txns.data?.length ?? 0) > 0) && (
                  <label className="field">
                    <span className="field-label">Related transaction <span className="opt">(optional)</span></span>
                    <select className="select" value={tcn} onChange={(e) => setTcn(e.target.value)}>
                      <option value="">None / not sure</option>
                      {txns.data?.filter((t) => t.tcn).map((t) => (
                        <option key={t.tcn} value={t.tcn}>{t.tcn} · {t.captureType} · {t.state}</option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="field">
                  <span className="field-label">Subject</span>
                  <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief summary" />
                </label>

                <label className="field">
                  <span className="field-label">How can we help?</span>
                  <textarea className="input" rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe the issue, including any error messages or steps to reproduce." />
                </label>

                {submitTicket.isError && (
                  <p className="cart-error">
                    {submitTicket.error instanceof ApiError ? submitTicket.error.message : 'Could not send your request. Please try again.'}
                  </p>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" type="submit" disabled={!canSend}>
                    {submitTicket.isPending ? 'Sending…' : 'Send request →'}
                  </button>
                  <button className="btn btn-ghost" type="button" onClick={() => setMode('chat')}>← Back to assistant</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </SupportContext.Provider>
  );
}
